// =============================================================================
// FE-20 — Delegation Permission Gate
// PRD §4 Feature G: "Only users with canDelegateApproval permission can create
// delegations (enforced in UI via permission check and backend via auth)."
//
// Exports:
//   DelegationPermissionGate   — Wraps children; shows "no permission" state
//                                 when the user lacks canDelegateApproval.
//   useDelegationPermission    — Lightweight hook for ad-hoc permission checks
//                                 (e.g. conditionally hiding individual buttons).
// =============================================================================

import React from 'react';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { usePermissions } from '@/contexts/PermissionContext';

// ---------------------------------------------------------------------------
// Hook — ad-hoc permission check
// ---------------------------------------------------------------------------

/**
 * Returns { canDelegate, isLoading } for the current user.
 * Use this when you need to conditionally render a button or link
 * without wrapping a whole subtree in the gate.
 */
export function useDelegationPermission(): { canDelegate: boolean; isLoading: boolean } {
  const { canDelegateApproval, isLoading } = usePermissions();
  return { canDelegate: canDelegateApproval(), isLoading };
}

// ---------------------------------------------------------------------------
// No-permission placeholder
// ---------------------------------------------------------------------------

/**
 * Shown in place of the protected content when the user lacks the
 * `canDelegateApproval` permission.  Displayed inside a Card so it fits
 * naturally wherever the gate is used.
 */
function NoPermissionState() {
  return (
    <AccessRestricted message="You don't have permission to create or manage approval delegations. Contact your administrator to request access." />
  );
}

// ---------------------------------------------------------------------------
// Gate props
// ---------------------------------------------------------------------------

interface DelegationPermissionGateProps {
  /**
   * Content to protect.  Only rendered when the user has `canDelegateApproval`.
   */
  children: React.ReactNode;
  /**
   * What to render when the user lacks permission.
   * Defaults to `<NoPermissionState />` (a Card with a friendly message).
   * Pass `null` to render nothing at all (silent hide).
   */
  fallback?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Gate component
// ---------------------------------------------------------------------------

/**
 * Wraps delegation UI that requires the `canDelegateApproval` permission.
 * While permissions are loading it renders nothing to avoid layout flash.
 *
 * Usage:
 *   <DelegationPermissionGate>
 *     <MyDelegationsContent />
 *   </DelegationPermissionGate>
 *
 * To silently hide (no placeholder card):
 *   <DelegationPermissionGate fallback={null}>
 *     <CreateButton />
 *   </DelegationPermissionGate>
 */
export const DelegationPermissionGate: React.FC<DelegationPermissionGateProps> = ({
  children,
  fallback,
}) => {
  const { canDelegate, isLoading } = useDelegationPermission();

  // While permissions are still loading, render nothing to avoid a flash
  // of the "no permission" state for authorised users.
  if (isLoading) return null;

  if (!canDelegate) {
    // `fallback` may be explicitly set to `null` to silence the UI entirely.
    return fallback !== undefined ? <>{fallback}</> : <NoPermissionState />;
  }

  return <>{children}</>;
};
